import { Fragment, Suspense, lazy, useState, type FormEvent } from 'react';
import { MdFeld } from './MdFeld';
import { Icon } from './Icon';
import { toast } from './toast';
import { Field, Select, Choice, Check, listParse, quellenFormat, quellenParse } from './fields';
import { mdHtml } from '../logik/md.js';
import { byId, NODE_TYPES, STATUS, statusOf, children, processTree, decisionsInTree, nodesOf } from '../logik/model.js';
import { useVerantwortlichVorschlaege } from './verantwortlich';
import { fmtDate } from '../logik/export/report.js';
import * as A from '../logik/actions.js';
import { DecisionBlock } from './Decisions';
import { Kommentare } from './Kommentare';
import { AufgabenBlock } from './AufgabenBlock';
import { useNavigate } from 'react-router';
import { useOffeneAufgaben } from '../offeneAufgaben';
import { datumKurz, faelligkeit, heuteIso } from '../aufgaben';
import { Avatar } from './Avatar';
import { sync, aufServer, darf as darfRolle } from '../sync';
import { frage } from './frage';

// Dokumente/Anhänge erst bei Bedarf laden (eigener Chunk, hält das Haupt-Bundle klein)
const DokumenteBlock = lazy(() => import('./Dokumente').then((m) => ({ default: m.DokumenteBlock })));
const AnhangVerlinken = lazy(() => import('./Dokumente').then((m) => ({ default: m.AnhangVerlinken })));
const Dokumente = (p: { bezugTyp: 'schritt' | 'prozess' | 'entscheidung'; bezugId: string }) => <Suspense fallback={null}><DokumenteBlock {...p} /></Suspense>;
const TestfallBlock = lazy(() => import('./Tests').then((m) => ({ default: m.TestfallBlock })));

/** Dokumente, Aufgaben, Testfälle (nur Schritte) und Kommentare brauchen ein gespeichertes Element (Bezug auf dem Server). */
const BEZUG = { schritt: 'nodes', prozess: 'processes', entscheidung: 'decisions' } as const;
function Verknuepftes({ bezugTyp, bezugId }: { bezugTyp: keyof typeof BEZUG; bezugId: string }) {
  if (!aufServer(BEZUG[bezugTyp], bezugId))
    return <p className="hint" data-testid="erst-speichern">Dokumente, Aufgaben und Kommentare sind möglich, sobald die Änderungen gespeichert sind.</p>;
  return (
    <>
      <Dokumente bezugTyp={bezugTyp} bezugId={bezugId} />
      <AufgabenBlock bezugTyp={bezugTyp} bezugId={bezugId} />
      {bezugTyp === 'schritt' && <Suspense fallback={null}><TestfallBlock schrittId={bezugId} /></Suspense>}
      <Kommentare bezugTyp={bezugTyp} bezugId={bezugId} />
    </>
  );
}

export type Auswahl = { type: 'node' | 'edge'; id: string } | null;

export interface PanelProps {
  data: any;
  pid: string | null;
  sel: Auswahl;
  edit: boolean;
  onClose: () => void;
  onSelect: (s: Auswahl) => void;
  onDrill: (pid: string | null) => void;
  onNavigate: (pid: string, nodeId?: string) => void;
}

/** Schritt nach Rückfrage löschen (Panel, Entf-Taste, Kontextmenü); true = gelöscht. */
export async function schrittLoeschen(n: { id: string; titel: string }) {
  if (!(await frage(`Schritt „${n.titel}“ löschen?`, { ja: 'Löschen', gefahr: true }))) return false;
  A.deleteNode(n.id);
  return true;
}

/** Verbindung nach Rückfrage löschen (Entf-Taste im Diagramm); true = gelöscht. */
export async function verbindungLoeschen(data: any, id: string) {
  const e = byId(data.edges, id);
  if (!e) return false;
  const von = byId(data.nodes, e.from)?.titel ?? '?', nach = byId(data.nodes, e.to)?.titel ?? '?';
  if (!(await frage(`Verbindung von „${von}“ nach „${nach}“ löschen?`, { ja: 'Löschen', gefahr: true }))) return false;
  A.deleteEdge(id);
  return true;
}

/** Prozess samt Unterprozessen nach Rückfrage löschen (Panel, Kontextmenü der Landkarte); true = gelöscht. */
export async function prozessLoeschen(data: any, p: { id: string; titel: string }) {
  const n = processTree(data, p.id).length;
  if (!(await frage(`Prozess „${p.titel}“${n > 1 ? ` inkl. ${n - 1} Unterprozess(en)` : ''} löschen?`, { ja: 'Löschen', gefahr: true }))) return false;
  A.deleteProcess(p.id);
  return true;
}

/** Notizen, Status und Entscheidungen in der Ansicht dürfen nur Admins und Verantwortliche ändern – im archivierten Projekt niemand. */
const darfPflegen = () => darfRolle('verantwortlich');

const Md = ({ text }: { text: string }) => <div className="md" dangerouslySetInnerHTML={{ __html: mdHtml(text) }} />;

function StatusPill({ status }: { status: string | null | undefined }) {
  const s = statusOf(status);
  if (!s) return null;
  return <span className="status-pill"><i style={{ background: s.farbe }} />{s.name}</span>;
}

/** Status direkt änderbar – auch außerhalb des Bearbeitungsmodus. */
const STATUS_OPTIONS = [{ value: '', label: 'Kein Status' }, ...STATUS.map((x: any) => ({ value: x.id, label: x.name, farbe: x.farbe }))];
function StatusSelect({ status, onChange, testid, darf }: { status: string | null | undefined; onChange: (v: any) => void; testid: string; darf: boolean }) {
  if (!darf) return <span data-testid={testid + '-anzeige'}>{statusOf(status) ? <StatusPill status={status} /> : <span className="hint">Kein Status</span>}</span>;
  return <Choice value={status || ''} options={STATUS_OPTIONS} onChange={onChange} testid={testid} />;
}

/** Neue Bahn direkt im Prozess anlegen. */
function NewLane({ onAdd }: { onAdd: (name: string) => void }) {
  const [name, setName] = useState('');
  const add = (e: FormEvent) => { e.preventDefault(); if (name.trim()) { onAdd(name.trim()); toast(`Bahn „${name.trim()}“ angelegt.`); setName(''); } };
  return (
    <form className="new-lane" onSubmit={add} style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
      <input className="inp" style={{ flex: 1 }} placeholder="Neue Bahn, z. B. Firmenname" value={name} onChange={(e) => setName(e.target.value)} data-testid="lane-new-name" />
      <button className="btn small" disabled={!name.trim()} data-testid="lane-new-add"><Icon name="plus" /> Anlegen</button>
    </form>
  );
}

function Quellen({ q }: { q: any[] | undefined }) {
  if (!q || !q.length) return null;
  return (
    <section><h3>Quellen</h3><ul className="md" style={{ paddingLeft: '18px', margin: 0 }}>
      {q.map((x, i) => <li key={i}>{x.anhangId
        ? <a href="#" title="Verlinktes Dokument herunterladen" data-testid="quelle-anhang"
            onClick={(e) => { e.preventDefault(); void import('./Dokumente').then((m) => m.herunterladen(sync.projektId, x.anhangId)); }}><Icon name="link" size={12} /> {x.dokument}</a>
        : x.dokument}{x.datum ? ' (' + fmtDate(x.datum) + ')' : ''}</li>)}</ul></section>
  );
}

function Notes({ value, onCommit, label = 'Notizen & Ergänzungen', darf }: { value: string; onCommit: (v: any) => void; label?: string; darf: boolean }) {
  return (
    <section className="notes"><h3>{label}</h3>
      {darf
        ? <MdFeld label="" wert={value ?? ''} onCommit={onCommit} platzhalter="Eigene Anmerkungen, Ergänzungen, Rückfragen …" leer="Klicken, um Anmerkungen, Ergänzungen oder Rückfragen zu schreiben …" testid="notes" />
        : <div data-testid="notes-anzeige">{value ? <Md text={value} /> : <p className="hint">Keine Notizen.</p>}</div>}
    </section>
  );
}

/** „Anna Muster, 07.10.26, 09:40“ – wer das Element zuletzt geändert hat. */
const stampText = (x: any) => (x.geaendertVon ? x.geaendertVon + (x.geaendertAm ? ', ' + new Date(x.geaendertAm).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '') : '');

export function Panel(props: PanelProps) {
  const { data, pid, sel, onClose } = props;
  let body;
  if (sel && sel.type === 'node' && byId(data.nodes, sel.id)) body = <NodePanel key={sel.id} {...props} n={byId(data.nodes, sel.id)} />;
  else if (sel && sel.type === 'edge' && byId(data.edges, sel.id)) body = <EdgePanel {...props} e={byId(data.edges, sel.id)} />;
  else if (pid && byId(data.processes, pid)) body = <ProcessPanel {...props} p={byId(data.processes, pid)} />;
  else body = <div className="empty-state">Kein Element ausgewählt.</div>;
  return (
    <div className="panel-inner" data-testid="panel">
      <button className="blatt-zurueck nur-handy" onClick={onClose} data-testid="blatt-zurueck"><Icon name="back" /> Zurück</button>
      <button className="btn icon close" title="Panel schließen" onClick={onClose} data-testid="panel-schliessen"><Icon name="x" /></button>
      {body}
    </div>
  );
}

// ---------------------------------------------------------------- Schritt
function NodePanel({ data, n, edit, onSelect, onDrill }: PanelProps & { n: any }) {
  const lane = byId(data.lanes, n.lane);
  const typ = NODE_TYPES.find((t: any) => t.id === n.typ);
  const set = (f: string) => (v: any) => A.setNodeField(n.id, f, v);
  const vorschlaege = useVerantwortlichVorschlaege(data);
  const dec = n.decisionId ? byId(data.decisions, n.decisionId) : null;
  const sub = n.subprocessId ? byId(data.processes, n.subprocessId) : null;
  const darf = darfPflegen();
  // Entscheidungen anlegen und bearbeiten wie alle Inhalte erst ab Verantwortlich (Mitwirkende kommentieren nur)
  const leser = !darf;
  // Neu angelegte Entscheidungen direkt im Bearbeitungszustand öffnen
  const [decEdit, setDecEdit] = useState(!leser && !!dec && dec.status !== 'entschieden' && dec.frage === 'Neue Entscheidung');
  const addDecisionAfter = () => {
    const id = A.addNode(n.processId, { typ: 'entscheidung', after: n.id, insert: true });
    toast('Entscheidung angelegt – bitte Frage und Varianten ausfüllen.');
    onSelect({ type: 'node', id });
  };

  if (!edit) {
    const details = ([
      ['Verantwortlich', n.verantwortlich],
      ['Belege', (n.belege || []).join(', ')],
      ['Input', n.input],
      ['Output', n.output],
      ['Geändert', stampText(n)],
    ] as [string, string][]).filter(([, v]) => v);
    return (
      <>
        <div className="kind">{typ?.name} · {lane?.name || n.lane}</div>
        <h2>{n.titel}</h2>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {n.typ !== 'start' && n.typ !== 'ende' && <StatusSelect status={n.status} onChange={set('status')} testid="node-status" darf={darf} />}
          {(n.ausbaustufe2 || n.nurZielbild) && <span className="chip" style={{ color: 'var(--pa-accent)' }}>Ausbaustufe 2</span>}
        </div>
        {n.systemIds?.length > 0 && <div style={{ marginTop: '8px' }}>{n.systemIds.map((s: string) => byId(data.systems, s)).filter(Boolean).map((s: any) => <span key={s.id} className="chip" title={s.beschreibung || ''}><i className="sw" style={{ background: s.farbe }} />{s.name}</span>)}</div>}
        {sub && <div className="actions"><button className="btn primary" onClick={() => onDrill(sub.id)}>Unterprozess öffnen: {sub.titel} <Icon name="chevron" /></button></div>}
        {dec && (
          <section data-testid="decision-section">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><h3 style={{ margin: 0 }}>Entscheidung</h3>
              {!leser && <button className={'btn small' + (decEdit ? ' on' : '')} data-testid="decision-edit" onClick={() => setDecEdit(!decEdit)}><Icon name={decEdit ? 'eye' : 'edit'} /> {decEdit ? 'Fertig' : 'Bearbeiten'}</button>}</div>
            <div style={{ marginTop: '8px' }}><DecisionBlock data={data} d={dec} edit={decEdit && !leser} compact /></div>
            <Verknuepftes bezugTyp="entscheidung" bezugId={dec.id} />
          </section>
        )}
        {n.beschreibung && <section><h3>Beschreibung</h3><Md text={n.beschreibung} /></section>}
        {n.zielbild && <section><h3>Zielbild / Ausbaustufe 2</h3><div className="zielbild-box"><Md text={n.zielbild} /></div></section>}
        {details.length > 0 && <section><h3>Details</h3><div className="meta-grid">{details.map(([k, v]) => <Fragment key={k}><div>{k}</div><div>{v}</div></Fragment>)}</div></section>}
        <Quellen q={n.quellen} />
        <Notes value={n.notizen} onCommit={set('notizen')} darf={darf} />
        <Verknuepftes bezugTyp="schritt" bezugId={n.id} />
        {n.typ !== 'ende' && !leser && <div className="actions"><button className="btn small" data-testid="add-decision" onClick={addDecisionAfter}><Icon name="decision" /> Offene Entscheidung danach einfügen</button></div>}
      </>
    );
  }

  const procs = data.processes.filter((p: any) => p.id !== n.processId);
  return (
    <>
      <div className="kind">Schritt bearbeiten</div>
      <Field label="Titel" value={n.titel} onCommit={set('titel')} testid="node-title" />
      <div className="grid2">
        <Select label="Typ" value={n.typ} options={NODE_TYPES.map((t: any) => ({ value: t.id, label: t.name }))} onChange={set('typ')} />
        <Select label="Bahn" value={n.lane} options={data.lanes.map((l: any) => ({ value: l.id, label: l.name, farbe: l.farbe }))} onChange={set('lane')} testid="node-lane" />
      </div>
      <Select label="Status" value={n.status} options={STATUS_OPTIONS} onChange={set('status')} />
      <label className="field"><span>Systeme</span><div className="chips-edit">
        {data.systems.map((s: any) => {
          const on = n.systemIds.includes(s.id);
          return <label key={s.id} className={on ? 'on' : ''} title={s.name + (s.beschreibung ? ' – ' + s.beschreibung : '')}><input type="checkbox" checked={on} onChange={() => A.setNodeField(n.id, 'systemIds', on ? n.systemIds.filter((x: string) => x !== s.id) : [...n.systemIds, s.id])} /><i className="sw" style={{ background: s.farbe, width: '8px', height: '8px', borderRadius: '50%', display: 'inline-block' }} />{s.kurz || s.name}</label>;
        })}
      </div></label>
      <Field label="Beschreibung (Markdown)" value={n.beschreibung} onCommit={set('beschreibung')} multiline tall testid="node-desc" />
      <Check label="Verbesserung in Ausbaustufe 2 geplant" checked={n.ausbaustufe2} onChange={set('ausbaustufe2')} />
      <Check label="Schritt nur im Zielbild anzeigen" checked={n.nurZielbild} onChange={set('nurZielbild')} />
      {(n.ausbaustufe2 || n.nurZielbild || n.zielbild) && <Field label="Zielbild / Ausbaustufe 2 (Markdown)" value={n.zielbild} onCommit={set('zielbild')} multiline />}
      <Field label="Verantwortlich" value={n.verantwortlich} onCommit={set('verantwortlich')} suggest={vorschlaege} testid="node-verantwortlich" hint="Projektmitglieder und bereits verwendete Namen werden vorgeschlagen." />
      <Field label="Belege (durch Komma getrennt)" value={n.belege} format={(v) => (v || []).join(', ')} parse={listParse(',')} onCommit={set('belege')} />
      <div className="grid2">
        <Field label="Input" value={n.input} onCommit={set('input')} />
        <Field label="Output" value={n.output} onCommit={set('output')} />
      </div>
      <Field label="Quellen (je Zeile: Dokument | Datum)" value={n.quellen} format={quellenFormat} parse={(v) => quellenParse(v, n.quellen)} onCommit={set('quellen')} multiline hint="Datum als TT.MM.JJJJ oder JJJJ-MM-TT; verlinkte Dokumente behalten ihren Link, solange der Name gleich bleibt" />
      <Suspense fallback={null}><AnhangVerlinken schrittId={n.id} quellen={n.quellen} onChange={set('quellen')} /></Suspense>
      <Field label="Notizen (Markdown)" value={n.notizen} onCommit={set('notizen')} multiline />
      <section>
        <h3>Unterprozess</h3>
        <Select label="Verknüpfter Prozess (Drilldown)" value={n.subprocessId} options={[{ value: '', label: '— keiner —' }, ...procs.map((p: any) => ({ value: p.id, label: p.titel }))]} onChange={(v) => { A.setNodeField(n.id, 'subprocessId', v); if (v && n.typ !== 'subprozess') A.setNodeField(n.id, 'typ', 'subprozess'); }} />
        <div className="actions">
          {!n.subprocessId && <button className="btn small" onClick={() => { const id = A.createSubprocessForNode(n.id); toast('Unterprozess angelegt.'); onDrill(id); }}><Icon name="layers" /> Neuen Unterprozess anlegen</button>}
          {sub && <button className="btn small" onClick={() => onDrill(sub.id)}>Öffnen <Icon name="chevron" /></button>}
        </div>
      </section>
      {dec && <section><h3>Entscheidung</h3><DecisionBlock data={data} d={dec} edit={true} /></section>}
      <section><h3>Aktionen</h3><div className="actions">
        <button className="btn small" onClick={() => onSelect({ type: 'node', id: A.addNode(n.processId, { after: n.id }) })}><Icon name="plus" /> Nachfolger</button>
        {(n.colHint != null || n.order != null) && <button className="btn small" onClick={() => A.resetNodePosition(n.id)}><Icon name="target" /> Position automatisch</button>}
        <button className="btn small danger" data-testid="delete-node" onClick={async () => { if (await schrittLoeschen(n)) onSelect(null); }}><Icon name="trash" /> Löschen</button>
      </div></section>
    </>
  );
}

// ---------------------------------------------------------------- Verbindung
function EdgePanel({ data, e, edit, onSelect }: PanelProps & { e: any }) {
  const t = byId(data.edgeTypes, e.typ);
  const from = byId(data.nodes, e.from), to = byId(data.nodes, e.to);
  return (
    <>
      <div className="kind">Verbindung</div>
      <h2>{from?.titel} → {to?.titel}</h2>
      {!edit && (
        <>
          <div className="meta-grid"><div>Art</div><div>{t?.name}</div>{e.label && <><div>Beschriftung</div><div>{e.label}</div></>}</div>
          {t?.beschreibung && <p className="hint">{t.beschreibung}</p>}
          <div className="actions"><button className="btn small" onClick={() => onSelect({ type: 'node', id: e.from })}>Zum Start</button><button className="btn small" onClick={() => onSelect({ type: 'node', id: e.to })}>Zum Ziel</button></div>
        </>
      )}
      {edit && (
        <section>
          <Select label="Art der Verbindung" value={e.typ} options={data.edgeTypes.map((x: any) => ({ value: x.id, label: x.name, farbe: x.farbe }))} onChange={(v) => A.setEdgeField(e.id, 'typ', v)} testid="edge-type" />
          <Field label="Beschriftung" value={e.label} onCommit={(v) => A.setEdgeField(e.id, 'label', v)} />
          <div className="actions">
            <button className="btn small" onClick={() => A.reverseEdge(e.id)}><Icon name="swap" /> Richtung umkehren</button>
            <button className="btn small danger" onClick={() => { A.deleteEdge(e.id); onSelect(null); }}><Icon name="trash" /> Löschen</button>
          </div>
        </section>
      )}
    </>
  );
}

// ---------------------------------------------------------------- Prozess
function BulkStatus({ data, p }: { data: any; p: any }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<string>(p.status || 'nicht_angeboten');
  const [withSub, setWithSub] = useState(true);
  const hasSub = children(data, p.id).length > 0;
  if (!open) return <div><button className="btn small" style={{ marginTop: '8px' }} onClick={() => setOpen(true)} data-testid="bulk-open">Status für alle Schritte setzen …</button></div>;
  return (
    <div className="bulk-box">
      <div className="field" style={{ marginBottom: '6px' }}><span>Neuer Status für alle Schritte dieses Prozesses</span>
        <Choice value={status} options={STATUS.map((x: any) => ({ value: x.id, label: x.name, farbe: x.farbe }))} onChange={setStatus} testid="bulk-status" /></div>
      {hasSub && <Check label="inklusive Unterprozesse" checked={withSub} onChange={setWithSub} />}
      <div className="actions" style={{ marginTop: '4px' }}>
        <button className="btn small primary" data-testid="bulk-apply" onClick={() => {
          const n = A.setStatusForSteps(p.id, status, hasSub && withSub);
          toast(`Status „${statusOf(status)?.name}“ für ${n} Schritte gesetzt.`);
          setOpen(false);
        }}>Übernehmen</button>
        <button className="btn small" onClick={() => setOpen(false)}>Abbrechen</button>
      </div>
      <p className="hint">Start, Ende und offene Entscheidungen bleiben unverändert. Rückgängig mit Strg/⌘+Z.</p>
    </div>
  );
}

/** Offene Aufgaben an den Schritten dieses Prozesses (Aufgaben am Prozess selbst zeigt der Block „Aufgaben“). */
function AufgabenAnSchritten({ data, p }: { data: any; p: any }) {
  const navigate = useNavigate();
  const alle = useOffeneAufgaben(sync.projektId);
  const schritte = new Map<string, any>(nodesOf(data, p.id, { zielbild: true }).map((n: any) => [n.id, n]));
  const liste = alle.filter((a) => a.bezugTyp === 'schritt' && a.bezugId && schritte.has(a.bezugId));
  if (!liste.length) return null;
  const heute = heuteIso();
  return (
    <section className="bezug-aufgaben" data-testid="aufgaben-an-schritten">
      <h3>Offene Aufgaben an Schritten<span className="badge-count" style={{ marginLeft: 6 }}>{liste.length}</span></h3>
      {liste.map((a) => (
        <div key={a.id} className="aufgabe-zeile klein" role="button" tabIndex={0} title="Aufgabe öffnen" data-testid={'schritt-aufgabe-' + a.id}
          onClick={() => navigate(`/p/${encodeURIComponent(sync.projektId)}/aufgaben/${encodeURIComponent(a.id)}`)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/p/${encodeURIComponent(sync.projektId)}/aufgaben/${encodeURIComponent(a.id)}`); }}>
          <span className="titel">{a.titel}<span className="hint"> · {schritte.get(a.bezugId!)?.titel}</span></span>
          {a.faellig && <span className={'faellig ' + faelligkeit(a.faellig, heute)} title={'Fällig am ' + a.faellig}>{datumKurz(a.faellig, heute)}</span>}
          {a.zustaendig && <Avatar email={a.zustaendig} size={20} />}
        </div>
      ))}
    </section>
  );
}

function ProcessPanel({ data, p, edit, onDrill, onNavigate }: PanelProps & { p: any }) {
  const subs = children(data, p.id);
  const decs = decisionsInTree(data, p.id).filter((d: any) => d.status !== 'entschieden');
  const set = (f: string) => (v: any) => A.setProcessField(p.id, f, v);
  const nodeCount = nodesOf(data, p.id, { zielbild: true }).length;
  const darf = darfPflegen();

  if (!edit) {
    return (
      <>
        <div className="kind">Prozess{p.geaendertVon ? <> · <span title="Letzte Änderung">{stampText(p)}</span></> : ''}</div>
        <h2>{p.titel}</h2>
        <StatusSelect status={p.status} onChange={set('status')} testid="proc-status" darf={darf} />
        {darf && <BulkStatus data={data} p={p} />}
        {p.kurz && <div style={{ fontWeight: 600, margin: '10px 0 0' }}><Md text={p.kurz} /></div>}
        {p.beschreibung && <section><h3>Beschreibung</h3><Md text={p.beschreibung} /></section>}
        {subs.length > 0 && <section><h3>Unterprozesse</h3>{subs.map((s: any) => <div key={s.id}><button className="btn small" style={{ marginBottom: '5px' }} onClick={() => onDrill(s.id)}>{s.titel} <Icon name="chevron" /></button></div>)}</section>}
        {decs.length > 0 && <section><h3>Offene Entscheidungen</h3>{decs.map((d: any) => <div key={d.id} style={{ marginBottom: '6px' }}><a href="#" onClick={(ev) => { ev.preventDefault(); onNavigate(d.processId, d.nodeId); }}>{d.id}: {d.frage}</a></div>)}</section>}
        <section><h3>Umfang</h3><div className="meta-grid"><div>Schritte</div><div>{nodeCount}</div><div>Bahnen</div><div>{p.laneIds.map((l: string) => byId(data.lanes, l)?.name).filter(Boolean).join(', ')}</div></div></section>
        <AufgabenAnSchritten data={data} p={p} />
        <Verknuepftes bezugTyp="prozess" bezugId={p.id} />
        <p className="hint" style={{ marginTop: '18px' }}>Tipp: Klicken Sie im Diagramm auf einen Schritt, um seine Beschreibung zu sehen. Kästen mit ⊞ öffnen den Unterprozess.</p>
      </>
    );
  }

  const tree = new Set(processTree(data, p.id).map((x: any) => x.id));
  const parents = data.processes.filter((x: any) => !tree.has(x.id));
  const laneIds: string[] = p.laneIds;
  const moveLane = (i: number, d: number) => {
    const arr = [...laneIds];
    const j = i + d;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    A.setProcessLanes(p.id, arr);
  };
  const unused = data.lanes.filter((l: any) => !laneIds.includes(l.id));
  const used = new Set(data.nodes.filter((x: any) => x.processId === p.id).map((x: any) => x.lane));
  return (
    <>
      <div className="kind">Prozess bearbeiten</div>
      <Field label="Titel" value={p.titel} onCommit={set('titel')} testid="proc-title" />
      <Field label="Kurzbeschreibung (Markdown)" value={p.kurz} onCommit={set('kurz')} multiline />
      <Field label="Beschreibung (Markdown)" value={p.beschreibung} onCommit={set('beschreibung')} multiline tall />
      <div className="grid2">
        <Select label="Status" value={p.status} options={STATUS_OPTIONS} onChange={set('status')} />
        <Select label="Übergeordneter Prozess" value={p.parentId} options={[{ value: '', label: '— Hauptprozess —' }, ...parents.map((x: any) => ({ value: x.id, label: x.titel }))]} onChange={set('parentId')} />
      </div>
      <section><h3>Bahnen in diesem Prozess</h3>
        {laneIds.map((id, i) => {
          const l = byId(data.lanes, id);
          return (
            <div key={id} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
              <i style={{ width: '10px', height: '10px', borderRadius: '3px', background: l?.farbe }} /><span style={{ flex: 1 }}>{l?.name || id}</span>
              <button className="btn icon small" title="nach oben" onClick={() => moveLane(i, -1)}><Icon name="up" /></button>
              <button className="btn icon small" title="nach unten" onClick={() => moveLane(i, 1)}><Icon name="down" /></button>
              <button className="btn icon small" title={used.has(id) ? 'Enthält Schritte – erst verschieben oder löschen' : 'aus diesem Prozess entfernen'} disabled={used.has(id)} onClick={() => A.setProcessLanes(p.id, laneIds.filter((x) => x !== id))} data-testid="lane-remove"><Icon name="x" /></button>
            </div>
          );
        })}
        {unused.length > 0 && (
          <div className="field" style={{ marginTop: '8px' }}><span>Vorhandene Bahn hinzufügen</span>
            <Choice value={null} placeholder="Bahn wählen oder tippen zum Filtern …" options={unused.map((l: any) => ({ value: l.id, label: l.name, farbe: l.farbe }))} onChange={(v) => v && A.setProcessLanes(p.id, [...laneIds, v])} testid="lane-add" /></div>
        )}
        <NewLane onAdd={(name) => A.addLaneToProcess(p.id, name)} />
        <p className="hint">Neue Bahn (z. B. eine weitere Firma) hier anlegen; sie steht danach in allen Prozessen zur Auswahl. Farbe und Namen ändern Sie unter Einstellungen › Bahnen. Bahnen mit Schritten lassen sich erst entfernen, wenn die Schritte verschoben oder gelöscht sind.</p>
      </section>
      <section><h3>Aktionen</h3><div className="actions">
        <button className="btn small" onClick={() => { const id = A.addProcess(p.id); onDrill(id); }}><Icon name="layers" /> Unterprozess anlegen</button>
        <button className="btn small" onClick={() => A.moveProcess(p.id, -1)}><Icon name="up" /> Reihenfolge</button>
        <button className="btn small" onClick={() => A.moveProcess(p.id, 1)}><Icon name="down" /> Reihenfolge</button>
        <button className="btn small danger" onClick={async () => {
          const parent = p.parentId;
          if (await prozessLoeschen(data, p)) onDrill(parent);
        }}><Icon name="trash" /> Prozess löschen</button>
      </div></section>
    </>
  );
}
